"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Check, ChevronDown, Plus, Settings, Bot, Key, Webhook } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useStore } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import { roleCanAdmin, workspaceRoleForUser } from "@/lib/workspaceMembers";
import { Modal } from "@/components/ui/Modal";
import { AgentsListModal } from "./AgentsListModal";
import { WebhooksModal } from "./WebhooksModal";
import { CreateWorkspaceModal } from "./CreateWorkspaceModal";

export function WorkspaceSwitcher() {
  const router = useRouter();
  const workspaces = useStore((s) => s.workspaces);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const currentUserId = useStore((s) => s.currentUserId);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const resetData = useStore((s) => s.resetData);
  const agentsModalRequest = useStore((s) => s.agentsModalRequest);

  const active = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];
  const canAdmin = roleCanAdmin(
    workspaceRoleForUser(workspaceMembers, active?.id ?? "", currentUserId)
  );
  const [open, setOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentsListOpen, setAgentsListOpen] = useState(false);
  const [webhooksOpen, setWebhooksOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // External triggers (e.g. empty-workspace quickstart) bump a counter;
  // we open the Agents modal in response.
  useEffect(() => {
    if (agentsModalRequest > 0 && canAdmin) setAgentsListOpen(true);
  }, [agentsModalRequest, canAdmin]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full rounded-md px-1.5 py-1 hover:bg-[var(--bg-hover)] transition-colors text-left"
      >
        <WorkspaceBadge workspace={active} size={24} />
        <span className="text-[13px] font-semibold truncate flex-1">{active?.name}</span>
        {active?.category === "dev" && <CategoryBadge category="dev" />}
        {active?.category === "personal" && <CategoryBadge category="personal" />}
        <ChevronDown size={13} className="text-[var(--fg-tertiary)] shrink-0" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.14 }}
            className="absolute z-40 left-0 top-full mt-1 w-[280px] rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-lg glass overflow-hidden"
          >
            <div className="p-1 max-h-[420px] overflow-y-auto scrollbar-thin">
              {(["client", "dev", "personal"] as const).map((cat) => {
                const group = workspaces.filter((w) => w.category === cat);
                if (group.length === 0) return null;
                const heading =
                  cat === "client" ? "Clients" : cat === "dev" ? "Dev" : "Personal";
                return (
                  <div key={cat} className="mb-1 last:mb-0">
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-[var(--fg-muted)] font-medium">
                      {heading}
                    </div>
                    {group.map((w) => (
                      <button
                        key={w.id}
                        onClick={() => {
                          setOpen(false);
                          // Close any modal that's currently open — without
                          // this, switching workspace via soft router.push
                          // leaves the modal state alive (it lives in this
                          // component which doesn't unmount on nav).
                          setAgentModalOpen(false);
                          setAgentsListOpen(false);
                          setWebhooksOpen(false);
                          setCreateOpen(false);
                          // Route first and let Workspace's slug effect switch
                          // the store. An eager store switch can briefly run
                          // against this workspace's task-filter URL and carry
                          // stale IDs into the destination workspace.
                          router.push(`/${w.slug}#home`);
                        }}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors",
                          w.id === active?.id
                            ? "bg-[var(--bg-active)]"
                            : "hover:bg-[var(--bg-hover)]"
                        )}
                      >
                        <WorkspaceBadge workspace={w} size={22} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-medium truncate">{w.name}</div>
                          <div className="text-[11px] text-[var(--fg-muted)] truncate">
                            {w.description}
                          </div>
                        </div>
                        {w.id === active?.id && (
                          <Check size={13} className="text-[var(--fg-secondary)] shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[var(--border)] p-1">
              <button
                onClick={() => {
                  setCreateOpen(true);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)]"
              >
                <Plus size={13} />
                Create workspace
              </button>
              {canAdmin && (
                <>
                  <button
                    onClick={() => {
                      setAgentModalOpen(true);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] transition-colors"
                  >
                    <Bot size={13} />
                    Agent instructions
                    <span className="ml-auto text-[10px] text-[var(--fg-muted)]">{active?.name}</span>
                  </button>
                  <button
                    onClick={() => {
                      setAgentsListOpen(true);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] transition-colors"
                  >
                    <Key size={13} />
                    Members, agents &amp; links
                    <span className="ml-auto text-[10px] text-[var(--fg-muted)]">{active?.name}</span>
                  </button>
                  <button
                    onClick={() => {
                      setWebhooksOpen(true);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] transition-colors"
                  >
                    <Webhook size={13} />
                    Webhooks
                    <span className="ml-auto text-[10px] text-[var(--fg-muted)]">{active?.name}</span>
                  </button>
                  {active?.category === "dev" && (
                    <button
                      onClick={() => {
                        if (confirm("Reset all data to seed?")) resetData();
                        setOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] transition-colors"
                    >
                      <Settings size={13} />
                      Reset demo data
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <CreateWorkspaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {active && (
        <>
          {canAdmin && (
            <>
              <AgentInstructionsModal
                workspace={active}
                open={agentModalOpen}
                onClose={() => setAgentModalOpen(false)}
              />
              <AgentsListModal
                workspace={active}
                open={agentsListOpen}
                onClose={() => setAgentsListOpen(false)}
              />
              <WebhooksModal
                workspace={active}
                open={webhooksOpen}
                onClose={() => setWebhooksOpen(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function AgentInstructionsModal({
  workspace,
  open,
  onClose,
}: {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Load when opened
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const sb = createClient();
    void sb
      .from("workspaces")
      .select("agent_instructions")
      .eq("id", workspace.id)
      .single()
      .then(({ data }) => {
        setContent(data?.agent_instructions ?? "");
        setLoading(false);
      });
  }, [open, workspace.id]);

  async function save() {
    setSaving(true);
    const sb = createClient();
    const { error } = await sb
      .from("workspaces")
      .update({ agent_instructions: content })
      .eq("id", workspace.id);
    setSaving(false);
    if (!error) setSavedAt(Date.now());
  }

  return (
    <Modal open={open} onClose={onClose} width={780}>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
        <Bot size={14} className="text-[var(--accent)]" />
        <h3 className="text-[13.5px] font-semibold">Agent instructions</h3>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">·</span>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">{workspace.name}</span>
        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
          Markdown · agents read this at session start
        </span>
      </div>
      <div className="p-5 space-y-2">
        <p className="text-[11.5px] text-[var(--fg-tertiary)] leading-relaxed">
          What the agent for this workspace needs to know — who the client is, cadence, tone,
          things to never touch. The MCP server returns this content via{" "}
          <code className="text-[var(--fg-secondary)] bg-[var(--bg-tertiary)] px-1 py-0.5 rounded">
            get_workspace_guide()
          </code>
          .
        </p>
        {loading ? (
          <div className="h-[420px] flex items-center justify-center text-[12.5px] text-[var(--fg-muted)]">
            Loading…
          </div>
        ) : (
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            spellCheck={false}
            className="w-full h-[420px] p-3 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border)] focus:border-[var(--accent)] outline-none text-[12.5px] font-mono text-[var(--fg-secondary)] resize-none scrollbar-thin"
            placeholder={`# ${workspace.name} — Agent Operating Manual\n\n## Who this is for\nYou're the agent for ${workspace.name}…\n\n## Your remit\n…\n\n## Cadence\n…\n\n## Tone\n…\n\n## Things to never touch\n…`}
          />
        )}
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)]">
        <span className="text-[11px] text-[var(--fg-muted)]">
          {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : ""}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
          >
            Close
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12.5px] font-medium"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function WorkspaceBadge({
  workspace,
  size = 24,
}: {
  workspace: Workspace | null | undefined;
  size?: number;
}) {
  if (!workspace) {
    return (
      <div
        className="rounded-md bg-[var(--bg-tertiary)]"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-md flex items-center justify-center text-white font-semibold shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(11, size * 0.55),
        background: `linear-gradient(135deg, ${workspace.color}, color-mix(in srgb, ${workspace.color} 55%, #000))`,
      }}
    >
      <span style={{ fontSize: Math.max(12, size * 0.6) }}>{workspace.icon}</span>
    </div>
  );
}

function CategoryBadge({ category }: { category: "dev" | "personal" }) {
  const isDev = category === "dev";
  return (
    <span
      className={
        "shrink-0 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider " +
        (isDev
          ? "bg-[var(--orange)]/15 text-[var(--orange)] border border-[var(--orange)]/40"
          : "bg-[var(--fg-muted)]/15 text-[var(--fg-tertiary)] border border-[var(--border-strong)]")
      }
    >
      {category}
    </span>
  );
}
